import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Package, Fingerprint, Ticket, LogOut, ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import { useCustomer } from "../context/CustomerContext";

const STATUS_COLOURS = {
  new: "bg-sky/20 text-sky-deep", in_production: "bg-lemon/40 text-plum-deep",
  completed: "bg-lilac/40 text-plum", shipped: "bg-forest/15 text-forest", cancelled: "bg-plum-deep/10 text-plum-deep/60",
};

export default function Account() {
  const { customer, logout } = useCustomer();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [blooms, setBlooms] = useState([]);
  const [vouchers, setVouchers] = useState([]);

  useEffect(() => {
    if (!customer) return;
    api.get("/account/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    api.get("/account/blooms").then((r) => setBlooms(r.data)).catch(() => {});
    api.get("/account/vouchers").then((r) => setVouchers(r.data)).catch(() => {});
  }, [customer]);

  if (customer === null) return <div className="pt-40 text-center text-plum-deep/60">Checking your profile…</div>;
  if (customer === false) return <Navigate to="/account/login" replace />;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="pt-28 md:pt-32 pb-24 px-5 md:px-10 bg-cream min-h-screen" data-testid="account-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center gap-4 mb-10">
          {customer.picture ? (
            <img src={customer.picture} alt="" className="w-16 h-16 rounded-full border-[3px] border-magenta object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-magenta text-cream flex items-center justify-center font-serif-display text-2xl border-[3px] border-plum-deep">
              {(customer.name || customer.email)[0].toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <p className="font-serif-display uppercase text-magenta text-[11px] tracking-[0.35em]">FOUNDRY PROFILE</p>
            <h1 className="font-serif-display uppercase text-plum-deep text-3xl md:text-4xl leading-none" data-testid="account-name">{customer.name || customer.email}</h1>
            <p className="text-sm text-plum-deep/60">{customer.email}</p>
          </div>
          <button onClick={doLogout} data-testid="account-logout-btn"
            className="inline-flex items-center gap-2 rounded-full bg-warmwhite border-2 border-plum-deep/20 px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-bold text-plum-deep hover:border-magenta">
            <LogOut className="w-3.5 h-3.5" /> SIGN OUT
          </button>
        </div>

        <section className="mb-10">
          <h2 className="flex items-center gap-2 font-serif-display uppercase text-plum-deep text-xl md:text-2xl mb-4"><Package className="w-5 h-5 text-magenta" /> MY ORDERS</h2>
          {orders === null ? <p className="text-plum-deep/60">Loading…</p> : orders.length === 0 ? (
            <div className="bg-warmwhite border-2 border-dashed border-plum-deep/20 rounded-3xl p-8 text-center" data-testid="account-no-orders">
              <p className="text-plum-deep/60 text-sm">No orders yet — your garden is waiting!</p>
              <div className="flex justify-center gap-3 mt-4">
                <Link to="/workshop" className="rounded-full bg-magenta text-cream px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-bold">BUILD TAGS</Link>
                <Link to="/supply-room" className="rounded-full bg-plum text-cream px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-bold">SUPPLY ROOM</Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="account-orders-list">
              {orders.map((o) => (
                <div key={o.id} className="bg-warmwhite border-2 border-plum-deep/10 rounded-2xl px-5 py-4 flex flex-wrap items-center gap-3" data-testid={`account-order-${o.order_number}`}>
                  <span className="font-bold text-sm">{o.order_number}</span>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full uppercase font-bold ${o.order_type === "supply" ? "bg-sky/25 text-sky-deep" : "bg-blush text-magenta-deep"}`}>
                    {o.order_type === "supply" ? "SUPPLY ROOM" : "WORKSHOP"}
                  </span>
                  <span className="text-xs text-plum-deep/60">{new Date(o.created_at).toLocaleDateString("en-NZ")}</span>
                  <span className="text-xs text-plum-deep/60 flex-1 truncate">
                    {o.items?.slice(0, 2).map((it) => it.title || `"${it.text_line1}"`).join(", ")}{o.items?.length > 2 ? "…" : ""}
                  </span>
                  <span className="font-bold text-sm">${o.total?.toFixed(2)}</span>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-bold ${STATUS_COLOURS[o.status] || "bg-plum-deep/10"}`}>{(o.status || "").replace("_", " ")}</span>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-bold ${o.payment_status === "paid" ? "bg-forest/15 text-forest" : "bg-lemon/50 text-plum-deep"}`}>{o.payment_status}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="grid md:grid-cols-2 gap-8">
          <section>
            <h2 className="flex items-center gap-2 font-serif-display uppercase text-plum-deep text-xl md:text-2xl mb-4"><Fingerprint className="w-5 h-5 text-sky-deep" /> MY BLOOM PASSPORTS</h2>
            {blooms.length === 0 ? (
              <p className="text-sm text-plum-deep/60 bg-warmwhite border-2 border-dashed border-plum-deep/20 rounded-2xl p-5" data-testid="account-no-blooms">
                No plant passports linked to this email yet. <Link to="/bloom-id" className="underline">Scan a Bloom ID</Link> to start one.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="account-blooms-list">
                {blooms.map((b) => (
                  <li key={b.id}>
                    <Link to={`/bloom-id?code=${encodeURIComponent(b.code)}`} className="flex items-center gap-3 bg-warmwhite border-2 border-plum-deep/10 rounded-2xl px-4 py-3 hover:border-sky transition-colors" data-testid={`account-bloom-${b.code}`}>
                      <span className="font-mono text-xs bg-plum text-lemon px-2.5 py-1 rounded-lg tracking-wider">{b.code}</span>
                      <span className="text-sm font-semibold text-plum-deep flex-1 truncate">{b.plant_name || "Unnamed bloom"}</span>
                      <span className="text-[10px] uppercase font-bold text-plum-deep/50">{b.status || ""}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 className="flex items-center gap-2 font-serif-display uppercase text-plum-deep text-xl md:text-2xl mb-4"><Ticket className="w-5 h-5 text-orange" /> MY VOUCHERS</h2>
            {vouchers.length === 0 ? (
              <p className="text-sm text-plum-deep/60 bg-warmwhite border-2 border-dashed border-plum-deep/20 rounded-2xl p-5" data-testid="account-no-vouchers">
                No gift vouchers on this email yet.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="account-vouchers-list">
                {vouchers.map((v) => (
                  <li key={v.code} className="flex items-center gap-3 bg-lemon/25 border-2 border-lemon rounded-2xl px-4 py-3" data-testid={`account-voucher-${v.code}`}>
                    <span className="font-mono text-sm bg-plum-deep text-lemon px-2.5 py-1 rounded-lg tracking-widest">{v.code}</span>
                    <span className="text-sm text-plum-deep flex-1">{v.sender_name ? `From ${v.sender_name}` : "Gift credit"}</span>
                    <span className="font-bold text-plum-deep">${Number(v.balance).toFixed(2)} left</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="mt-12 text-center">
          <Link to="/supply-room" className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-7 py-3.5 font-serif-display uppercase text-sm tracking-[0.16em] font-bold hover:bg-magenta transition-colors" data-testid="account-visit-supply">
            <ShoppingBag className="w-4 h-4" /> VISIT THE SUPPLY ROOM
          </Link>
        </div>
      </div>
    </div>
  );
}
